import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { SubscriptionModalComponent } from '@core/components/subscription-modal/subscription-modal.component';
import { SubscriptionPaywallModalComponent } from '@core/components/subscription-paywall-modal/subscription-paywall-modal.component';
import { PaymentProcessingModalComponent } from '@core/components/payment-processing-modal/payment-processing-modal.component';
import { BehaviorSubject, Observable } from 'rxjs';
import { SubscriptionHttpService } from '@core/_services/subscription/http/subscription-http.service';
import { finalize } from 'rxjs/operators';
import { MessageResponse } from '@core/models/interfaces/message_response.interface';
import { AuthService } from '@modules/auth/_services/auth.service';
import { PaymentSubscriptionStatus } from '@core/models/interfaces/subscription.interface';

@Injectable({ providedIn: 'root' })
export class SubscriptionService {
  constructor(
    private dialog: MatDialog,
    private subscriptionHttpService: SubscriptionHttpService,
    private authService: AuthService
  ) {
  }

  private _isLoading$ = new BehaviorSubject<boolean>(false);

  get isLoading$() {
    return this._isLoading$.asObservable();
  }

  openPaywallDialog() {
    this.dialog.open(SubscriptionPaywallModalComponent, { disableClose: true });
  }

  openSubscriptionDialog(disableClose: boolean = false) {
    this.dialog.open(SubscriptionModalComponent, { disableClose });
  }

  openPaymentProcessingModal(isCancel: boolean = false) {
    this.dialog.open(PaymentProcessingModalComponent, { disableClose: true, data: { isCancel } });
  }

  startPayment(): Observable<MessageResponse> {
    this._isLoading$.next(true);

    return this.subscriptionHttpService.startPayment().pipe(
      finalize(() => this._isLoading$.next(false))
    );
  }

  cancelSubscription(): Observable<MessageResponse> {
    this._isLoading$.next(true);

    return this.subscriptionHttpService.cancelSubscription().pipe(
      finalize(() => {
        this._isLoading$.next(false);
        this.openPaymentProcessingModal(true);
      })
    );
  }

  /**
   * Checks if the subscription has expired and shows paywall if needed
   * If subscription date has passed, opens the subscription paywall
   * @returns boolean - Whether the paywall was shown
   */
  checkAndShowPaywall(): boolean {
    const currentUser = this.authService.currentUserValue;

    // Don't show paywall if user is not logged in
    if (!currentUser?.profile?.company?.subscription) {
      return false;
    }

    const validUntil = currentUser.profile.company.subscription.valid_until;
    if (!validUntil) {
      return false;
    }

    const validUntilDate = new Date(validUntil);
    const currentDate = new Date();

    const timeDiff = validUntilDate.getTime() - currentDate.getTime();

    // Show paywall only if the subscription has expired (date has passed)
    if (currentUser.profile.company.subscription.status === PaymentSubscriptionStatus.INACTIVE && timeDiff <= 0) {
      this.openPaywallDialog();
      return true;
    }

    return false;
  }

  /**
   * Checks if payment is pending and shows processing modal if needed
   * @returns boolean - Whether the processing modal was shown
   */
  checkAndShowPaymentProcessing(): boolean {
    const currentUser = this.authService.currentUserValue;

    // Don't show processing modal if user is not logged in
    if (!currentUser?.profile?.company) {
      return false;
    }

    // Show processing modal if payment is pending
    if (currentUser.profile.company.pending_payment || currentUser.profile.company.pending_cancel) {
      this.openPaymentProcessingModal(currentUser.profile.company.pending_cancel);
      return true;
    }

    return false;
  }
}
